/**
 * Auth-aware fetcher — single chokepoint for every API call from the FE.
 *
 * Concerns owned by this module (research-B §5.3, §8.1; PLAN tasks 5.5/5.6;
 * customer Q53/Q56; FIX-COOKIE-PATH; FIX-CSRF):
 *
 * 1. **Access token**: stored module-scope (NOT React context, so it
 *    survives unmount). `getAccess()` / `setAccess()` are the only public
 *    accessors. Set on login + silent-refresh; cleared on logout / hard 401.
 *
 * 2. **CSRF (FIX-CSRF + Wave-5 fix 5.x.1)**: state-mutating methods
 *    (POST/PUT/PATCH/DELETE) attach the CSRF double-submit header. The
 *    header NAME and the cookie NAME both come from `@aris/shared` so FE
 *    and BE cannot drift (the prior bug: FE sent `X-CSRF`, BE read
 *    `x-csrf-token`, silent-refresh failed in prod). The cookie is set by
 *    the BE on first contact. `credentials: 'include'` so the refresh +
 *    CSRF cookies travel.
 *
 * 3. **401-refresh dedup**: when one in-flight request gets 401, parallel
 *    401s from sibling queries WAIT on the SAME refresh promise — only one
 *    /auth/refresh call ever fires. On success, every waiter retries once.
 *    On second 401 we clear access and redirect to /login.
 *
 * 4. **409 / 429 / 5xx / network**: typed errors thrown so React Query's
 *    `onError` can route to a toast. Toasts here are fire-and-forget; we
 *    never silently swallow.
 *
 * 5. **Base URL**: `import.meta.env.VITE_API_BASE_URL`, defaulting to the
 *    `/api/v1` contract (D14 / FIX-COOKIE-PATH). Hard-coding here matches
 *    the refresh cookie path on the server (`Path=/api/v1`).
 */
import { CSRF_COOKIE_NAME, CSRF_HEADER_NAME } from '@aris/shared';

import { toast } from '@/components/ui/toast';

// ─── Module-scope access token store ─────────────────────────────────────
// Keep the private state in a closure so callers cannot bypass `setAccess`.

let accessToken: string | null = null;

/** Returns the current in-memory access token, or null if signed out. */
export function getAccess(): string | null {
  return accessToken;
}

/** Stores or clears the in-memory access token (pass `null` to clear). */
export function setAccess(token: string | null): void {
  accessToken = token;
}

/** Hard reset of in-memory auth — used by logout + hard-401 paths. */
export function clearAccess(): void {
  accessToken = null;
}

// ─── Error envelope (matches API error filter) ───────────────────────────

export interface ApiErrorEnvelope {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly envelope: ApiErrorEnvelope | null;
  /**
   * ⚠️ ЕДИНСТВЕННЫЙ ИСТОЧНИК ПРАВДЫ О ТОМ, УВИДЕЛ ЛИ ОТКАЗ ПОЛЬЗОВАТЕЛЬ.
   * Ставится ЗДЕСЬ, ровно там, где `apiFetch` сам показал сообщение (обрыв
   * связи, конфликт версий, `429`, смена роли, увод на `/login`), и читается
   * помощником `showApiError` — чтобы вызывающий не напечатал второе
   * сообщение поверх первого.
   *
   * Почему флаг, а не перечень случаев у вызывающего: перечень — это КОПИЯ
   * знания фетчера, и она разойдётся с ним молча при первой же правке
   * фетчера. Разница не умозрительная: `project-archive.dialog` получает
   * `409` то как `OPTIMISTIC_LOCK_CONFLICT` (фетчер его показывает), то с
   * другим кодом (не показывает) — по статусу эти два случая неразличимы,
   * по флагу различать не надо.
   */
  readonly alreadyReported: boolean;

  constructor(
    status: number,
    message: string,
    envelope: ApiErrorEnvelope | null,
    alreadyReported = false,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = envelope?.error?.code;
    this.envelope = envelope;
    this.alreadyReported = alreadyReported;
  }
}

// ─── CSRF cookie reader ──────────────────────────────────────────────────

/**
 * Read the CSRF double-submit cookie. Server seeds it; FE echoes it back
 * via the `CSRF_HEADER_NAME` (`x-csrf-token`) header on state-mutating
 * calls. The header + cookie names come from `@aris/shared`, so FE/BE
 * cannot diverge (Wave-5 fix 5.x.1).
 *
 * Returns empty string if the cookie is missing — cold-boot path hits the
 * session bootstrap (App.tsx) first, so by the time a mutation fires we
 * expect the cookie to exist.
 */
function readCsrfCookie(): string {
  if (typeof document === 'undefined') return '';
  const raw = document.cookie ?? '';
  for (const part of raw.split(';')) {
    const [k, ...rest] = part.trim().split('=');
    if (k === CSRF_COOKIE_NAME) return decodeURIComponent(rest.join('='));
  }
  return '';
}

const STATE_MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// ─── Base URL (D14 / FIX-COOKIE-PATH) ────────────────────────────────────

function resolveBaseUrl(): string {
  // Vite injects import.meta.env at build time. Tests run via vitest +
  // jsdom; vitest exposes the same import.meta.env shape.
  const fromEnv =
    typeof import.meta !== 'undefined' &&
    (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_API_BASE_URL;
  return (fromEnv && fromEnv.length > 0 ? fromEnv : '/api/v1').replace(/\/$/, '');
}

export const API_BASE_URL = resolveBaseUrl();

// ─── Single-flight refresh promise ───────────────────────────────────────

let refreshInFlight: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  // POST /auth/refresh — cookie-based; BE rotates the refresh cookie and
  // returns a fresh access token in the JSON body.
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        [CSRF_HEADER_NAME]: readCsrfCookie(),
      },
    });
    if (!res.ok) return false;
    const body = (await res.json().catch(() => null)) as { accessToken?: string } | null;
    if (!body?.accessToken) return false;
    setAccess(body.accessToken);
    return true;
  } catch {
    return false;
  }
}

/**
 * Trigger (or join) the singleton refresh. Multiple parallel callers share
 * the same Promise so only one /auth/refresh request ever fires per cycle.
 */
async function refreshAccessToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// ─── Toast helpers (kept tiny so the test surface is small) ──────────────

const TOASTS = {
  optimisticLockConflict: 'Данные изменились, обновите страницу',
  rateLimited: 'Слишком много попыток, подождите',
  networkFailure: 'Нет связи',
  staleJwtRoleChanged: 'Ваша роль изменилась — войдите заново',
} as const;

/**
 * Тексты для `ApiError.message`.
 *
 * `ApiError.message` — это НЕ внутренняя диагностика: почти каждая форма в
 * приложении на неудачном запросе делает `toast(err.message)`. Поэтому здесь
 * не может быть служебных английских строк («network failure», «session
 * expired») и не может быть `res.statusText` — HTTP-фраза вроде
 * «Unprocessable Entity» приходит от сервера/прокси на английском и уезжает
 * пользователю как есть, когда тело ответа не наш JSON-конверт (502/504 от
 * Caddy, обрыв на прокси).
 */
const API_ERROR_MESSAGES = {
  networkFailure: 'Нет связи с сервером. Проверьте подключение и повторите',
  sessionExpired: 'Сессия истекла. Войдите заново',
  /** Ответ без нашего конверта — сказать можно только «повторите». */
  unexpected: 'Не удалось выполнить запрос. Повторите попытку позже',
} as const;

// ─── apiFetch ────────────────────────────────────────────────────────────

export interface ApiFetchInit extends Omit<RequestInit, 'body'> {
  /** JSON body — stringified automatically. Use `rawBody` for non-JSON. */
  body?: unknown;
  /** Pre-serialized body (e.g. FormData). Bypasses JSON.stringify. */
  rawBody?: BodyInit;
  /**
   * ФОНОВЫЙ запрос — тот, который человек не просил (опрос по таймеру).
   *
   * На отказе такого запроса фетчер НЕ показывает тост «слишком много
   * запросов» и «нет связи»: предупреждение без действия человека — шум, а
   * при исчерпанном бюджете запросов оно всплывало бы в каждой вкладке на
   * каждом такте. Ошибка при этом не глотается — она уходит вызывающему с
   * `alreadyReported = false`, и он показывает её там, где человек её ждёт.
   *
   * ⚠️ Увод на вход (истёкшая сессия, сменившаяся роль) флаг НЕ отключает:
   * это не шум, а состояние, из которого вкладка сама не выйдет.
   */
  background?: boolean;
}

/**
 * `safeFetch` — single-source-of-truth wrapper around `fetch` that
 * applies our two project-wide rules consistently:
 *
 *   1. `AbortError` MUST propagate untouched. Callers (React StrictMode
 *      effect cleanup, navigation away mid-fetch) rely on the
 *      AbortController contract; collapsing the abort into an ApiError
 *      breaks cancellation semantics.
 *   2. Every other thrown fetch error becomes a `(0, 'network failure')`
 *      `ApiError` with a single user-visible toast.
 *
 * Wave-5 fix 5.x.10 / code-review F-3: previously this logic was
 * duplicated across the initial fetch and the post-refresh retry —
 * fertile ground for future drift (e.g. a toast change applied to one
 * branch but not the other). Centralising is the obvious cleanup.
 */
async function safeFetch(url: string, init: RequestInit, background = false): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (err) {
    if ((err as { name?: string } | null)?.name === 'AbortError') throw err;
    if (background) {
      // Фоновый запрос: тоста нет, и вызывающему честно сказано «не показано».
      throw new ApiError(0, API_ERROR_MESSAGES.networkFailure, null, false);
    }
    toast(TOASTS.networkFailure, { variant: 'error' });
    // Сообщение показано строкой выше — вызывающему печатать нечего.
    throw new ApiError(0, API_ERROR_MESSAGES.networkFailure, null, true);
  }
}

/**
 * Побочные действия фетчера на ошибочном ответе — В ОДНОМ МЕСТЕ.
 *
 * Возвращает `true`, если пользователь УЖЕ увидел сообщение (или его уводят
 * на `/login`, и сообщение поверх увода было бы шумом). Значение уезжает в
 * `ApiError.alreadyReported`, и по нему `showApiError` решает, печатать ли
 * своё.
 *
 * ⚠️ ФУНКЦИЯ ОДНА, А ВЫЗОВОВ ДВА — И ЭТО ГЛАВНОЕ. Прежде тот же разбор стоял
 * дважды: в `apiFetch` и в `apiFetchRaw`. Копии УЖЕ РАЗОШЛИСЬ — в `apiFetchRaw`
 * не было ветки `stale_jwt_role_changed`, то есть на сыром маршруте протухшая
 * роль не уводила на вход вовсе. Слияние копий эту ветку возвращает обоим;
 * расширение намеренное и названо в отчёте волны, а не проведено молча.
 */
function surfaceGlobalError(
  status: number,
  envelope: ApiErrorEnvelope | null,
  background = false,
): boolean {
  if (status === 409 && envelope?.error?.code === 'OPTIMISTIC_LOCK_CONFLICT') {
    toast(TOASTS.optimisticLockConflict, { variant: 'warning' });
    return true;
  }
  if (status === 429) {
    // Фоновый опрос об исчерпанном бюджете молчит (см. `ApiFetchInit.background`).
    if (background) return false;
    toast(TOASTS.rateLimited, { variant: 'warning' });
    return true;
  }
  if (status === 403 && envelope?.error?.code === 'stale_jwt_role_changed') {
    // DA-9: backend declared the JWT role no longer matches the DB. The
    // user must re-authenticate so the new token reflects the current
    // accessLevel. Clear in-memory access + bounce to /login with a reason
    // banner. Avoid infinite loops on the login page itself.
    clearAccess();
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      toast(TOASTS.staleJwtRoleChanged, { variant: 'warning' });
      window.location.assign('/login?reason=role_changed');
      return true;
    }
    // Уже на входе: ни сообщения, ни увода не было — значит НЕ показано, и
    // вызывающий вправе сказать своё. `return true` здесь был бы враньём.
    return false;
  }
  return false;
}

async function readEnvelope(res: Response): Promise<ApiErrorEnvelope | null> {
  const ctype = res.headers.get('content-type') ?? '';
  if (!ctype.includes('application/json')) return null;
  try {
    return (await res.json()) as ApiErrorEnvelope;
  } catch {
    return null;
  }
}

/**
 * The single API call primitive. All API hooks (auth, users, …) wrap this.
 * Returns parsed JSON for 2xx; throws `ApiError` for everything else.
 *
 * @param path  Relative to API_BASE_URL — e.g. `/auth/login`.
 * @param init  Standard fetch init plus `body` (JSON-serialized) or `rawBody`.
 */
export async function apiFetch<T>(path: string, fullInit: ApiFetchInit = {}): Promise<T> {
  // `background` — наш флаг, а не поле `RequestInit`: до `fetch` он не доходит.
  const { background = false, ...init } = fullInit;
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const method = (init.method ?? 'GET').toUpperCase();
  const isMutating = STATE_MUTATING_METHODS.has(method);

  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (init.body !== undefined && !init.rawBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (isMutating) headers.set(CSRF_HEADER_NAME, readCsrfCookie());
  const access = getAccess();
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const body =
    init.rawBody !== undefined
      ? init.rawBody
      : init.body !== undefined
        ? JSON.stringify(init.body)
        : undefined;

  // Initial fetch — Abort/network handling centralised in `safeFetch`
  // (Wave-5 fix 5.x.10 / code-review F-3).
  let res = await safeFetch(
    url,
    {
      ...init,
      method,
      headers,
      body,
      credentials: init.credentials ?? 'include',
    },
    background,
  );

  // 401 → try refresh once, then retry once. Second 401 → clear + redirect.
  if (res.status === 401 && !path.startsWith('/auth/login') && !path.startsWith('/auth/refresh')) {
    const ok = await refreshAccessToken();
    if (ok) {
      const retryHeaders = new Headers(headers);
      const fresh = getAccess();
      if (fresh) retryHeaders.set('Authorization', `Bearer ${fresh}`);
      if (isMutating) retryHeaders.set(CSRF_HEADER_NAME, readCsrfCookie());
      res = await safeFetch(
        url,
        {
          ...init,
          method,
          headers: retryHeaders,
          body,
          credentials: init.credentials ?? 'include',
        },
        background,
      );
      if (res.status === 401) {
        clearAccess();
        if (typeof window !== 'undefined') window.location.assign('/login');
        const env = await readEnvelope(res);
        throw new ApiError(401, API_ERROR_MESSAGES.sessionExpired, env, true);
      }
    } else {
      clearAccess();
      if (typeof window !== 'undefined') window.location.assign('/login');
      const env = await readEnvelope(res);
      throw new ApiError(401, API_ERROR_MESSAGES.sessionExpired, env, true);
    }
  }

  if (res.ok) {
    if (res.status === 204) return undefined as T;
    const ctype = res.headers.get('content-type') ?? '';
    if (!ctype.includes('application/json')) return undefined as T;
    return (await res.json()) as T;
  }

  // Error path — parse envelope, side-effect for the special codes, throw.
  const envelope = await readEnvelope(res);
  const reported = surfaceGlobalError(res.status, envelope, background);

  throw new ApiError(
    res.status,
    envelope?.error?.message ?? API_ERROR_MESSAGES.unexpected,
    envelope,
    reported,
  );
}

/**
 * Same wire path as `apiFetch`, but returns the raw `Response` instead of
 * the parsed JSON body. Used when the caller needs to read response
 * headers (e.g. Phase-5 dashboard mirrors `X-Aris-Dashboard-Stale`).
 *
 * Throws `ApiError` for non-2xx the same way `apiFetch` does — only the
 * happy path differs (caller decodes the body itself, typically via
 * `await res.json()` followed by a zod parse).
 */
export async function apiFetchRaw(path: string, init: ApiFetchInit = {}): Promise<Response> {
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const method = (init.method ?? 'GET').toUpperCase();
  const isMutating = STATE_MUTATING_METHODS.has(method);

  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (init.body !== undefined && !init.rawBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (isMutating) headers.set(CSRF_HEADER_NAME, readCsrfCookie());
  const access = getAccess();
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const body =
    init.rawBody !== undefined
      ? init.rawBody
      : init.body !== undefined
        ? JSON.stringify(init.body)
        : undefined;

  let res = await safeFetch(url, {
    ...init,
    method,
    headers,
    body,
    credentials: init.credentials ?? 'include',
  });

  // 401 retry-after-refresh — same single-flight semantics as apiFetch.
  if (res.status === 401 && !path.startsWith('/auth/login') && !path.startsWith('/auth/refresh')) {
    const ok = await refreshAccessToken();
    if (ok) {
      const retryHeaders = new Headers(headers);
      const fresh = getAccess();
      if (fresh) retryHeaders.set('Authorization', `Bearer ${fresh}`);
      if (isMutating) retryHeaders.set(CSRF_HEADER_NAME, readCsrfCookie());
      res = await safeFetch(url, {
        ...init,
        method,
        headers: retryHeaders,
        body,
        credentials: init.credentials ?? 'include',
      });
      if (res.status === 401) {
        clearAccess();
        if (typeof window !== 'undefined') window.location.assign('/login');
        const env = await readEnvelope(res);
        throw new ApiError(401, API_ERROR_MESSAGES.sessionExpired, env, true);
      }
    } else {
      clearAccess();
      if (typeof window !== 'undefined') window.location.assign('/login');
      const env = await readEnvelope(res);
      throw new ApiError(401, API_ERROR_MESSAGES.sessionExpired, env, true);
    }
  }

  if (res.ok) return res;

  const envelope = await readEnvelope(res);
  const reported = surfaceGlobalError(res.status, envelope);

  throw new ApiError(
    res.status,
    envelope?.error?.message ?? API_ERROR_MESSAGES.unexpected,
    envelope,
    reported,
  );
}

// ─── Test-only seam — lets specs reset the singleton refresh promise ─────

/** @internal Test helper. NOT exported from index. */
export function __resetForTests(): void {
  accessToken = null;
  refreshInFlight = null;
}
